import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, BookOpen, List, CalendarDays, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import type { PatientNoteTherapist, NoteReplyTherapist } from "../../../domain/PatientNote";
import { listNoteReplies, listPatientNotes, markNoteRead, replyToNote } from "../../../application/patientNotes";
import Button from "../../../../../shared/ui/button/Button";
import NotionEditor from "../../../../../shared/ui/form/input/NotionEditor";
import NoteRepliesSection, { type NoteReply } from "../../../../../shared/ui/notes/NoteRepliesSection";
import { toLocalDateKey } from "../../../../../shared/utils/date";

function getPlainText(body: string): string {
  try {
    const blocks = JSON.parse(body);
    if (Array.isArray(blocks)) {
      for (const block of blocks) {
        const texts: string[] = [];
        if (Array.isArray(block.content)) {
          for (const item of block.content) {
            if (item.type === "text" && item.text) texts.push(item.text);
          }
        }
        const line = texts.join("").trim();
        if (line) return line;
      }
      return "";
    }
  } catch { /* fall through */ }
  return body.replace(/<[^>]*>/g, "").trim();
}

function getNoteTitle(note: PatientNoteTherapist): string {
  if (note.title?.trim()) return note.title.trim();
  return getPlainText(note.body).split(/\n/)[0].trim().slice(0, 60);
}

function noteToDateKey(note: PatientNoteTherapist): string {
  return note.note_date ?? note.created_at.split("T")[0];
}

function buildMonthCells(year: number, month: number): (number | null)[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startOffset = (first.getDay() + 6) % 7;
  const cells: (number | null)[] = Array(startOffset).fill(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function CalendarPanel({
  notes,
  selectedDay,
  onSelectDay,
  currentMonth,
  onMonthChange,
}: {
  notes: PatientNoteTherapist[];
  selectedDay: string | null;
  onSelectDay: (day: string) => void;
  currentMonth: Date;
  onMonthChange: (d: Date) => void;
}) {
  const { i18n } = useTranslation();
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const cells = buildMonthCells(year, month);
  const today = toLocalDateKey();

  const notesByDate: Record<string, number> = {};
  notes.forEach((n) => {
    const key = noteToDateKey(n);
    notesByDate[key] = (notesByDate[key] ?? 0) + 1;
  });

  const weekHeaders = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(2024, 0, 1 + i);
    return d.toLocaleDateString(i18n.language, { weekday: "short" });
  });

  return (
    <div className="flex flex-col overflow-hidden px-3 pb-3 pt-2">
      <div className="flex items-center justify-between mb-2 shrink-0">
        <button
          onClick={() => onMonthChange(new Date(year, month - 1))}
          className="p-1.5 rounded-lg text-content-tertiary hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-semibold text-content-secondary capitalize">
          {currentMonth.toLocaleDateString(i18n.language, { month: "long", year: "numeric" })}
        </span>
        <button
          onClick={() => onMonthChange(new Date(year, month + 1))}
          className="p-1.5 rounded-lg text-content-tertiary hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 mb-1 shrink-0">
        {weekHeaders.map((h) => (
          <div key={h} className="text-center text-xs text-content-tertiary font-medium py-1 capitalize">
            {h}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((day, i) => {
          if (!day) return <div key={`pad-${i}`} className="h-9" />;
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const count = notesByDate[dateStr] ?? 0;
          const isToday = dateStr === today;
          const isSelected = dateStr === selectedDay;
          return (
            <button
              key={dateStr}
              onClick={() => onSelectDay(dateStr)}
              className={`relative flex flex-col items-center justify-center h-9 rounded-lg text-xs transition-colors
                ${isSelected
                  ? "bg-brand-500 text-white font-semibold"
                  : isToday
                  ? "ring-2 ring-brand-400 ring-inset text-brand-600 dark:text-brand-400 font-semibold"
                  : "hover:bg-gray-100 dark:hover:bg-white/5 text-content-secondary"
                }`}
            >
              {day}
              {count > 0 && (
                <span className={`absolute bottom-0.5 w-1.5 h-1.5 rounded-full ${isSelected ? "bg-white/80" : "bg-brand-400"}`} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function PatientNotesTab({ patientId, patientName }: { patientId: string; patientName?: string }) {
  const { t, i18n } = useTranslation();
  const [notes, setNotes] = useState<PatientNoteTherapist[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<PatientNoteTherapist | null>(null);
  const [markingRead, setMarkingRead] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [mobileView, setMobileView] = useState<"nav" | "content">("nav");
  const [replies, setReplies] = useState<NoteReplyTherapist[]>([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [replySubmitting, setReplySubmitting] = useState(false);

  useEffect(() => {
    listPatientNotes(patientId)
      .then((data) => {
        setNotes(data);
        if (data.length > 0) setSelected(data[0]);
      })
      .finally(() => setLoading(false));
  }, [patientId]);

  const selectedNoteId = selected?.id ?? null;

  useEffect(() => {
    if (!selectedNoteId) { setReplies([]); return; }
    setRepliesLoading(true);
    listNoteReplies(patientId, selectedNoteId)
      .then((data) => setReplies(data))
      .catch(() => setReplies([]))
      .finally(() => setRepliesLoading(false));
  }, [patientId, selectedNoteId]);

  const handleSubmitReply = async (body: string) => {
    if (!selectedNoteId) return;
    setReplySubmitting(true);
    try {
      const data = await replyToNote(patientId, selectedNoteId, body);
      setReplies((prev) => [...prev, data]);
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleMarkRead = async () => {
    if (!selected) return;
    setMarkingRead(true);
    try {
      const data = await markNoteRead(patientId, selected.id);
      const updated = { ...selected, read_by_therapist_at: data.read_by_therapist_at };
      setNotes((prev) => prev.map((n) => (n.id === selected.id ? updated : n)));
      setSelected(updated);
    } finally {
      setMarkingRead(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-brand-500 opacity-50" />
      </div>
    );
  }

  if (notes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-content-tertiary">
        <BookOpen className="w-8 h-8 opacity-40" />
        <p className="text-sm">{t("patient.patientNotes.empty")}</p>
      </div>
    );
  }

  const dayNotes = selectedDay ? notes.filter((n) => noteToDateKey(n) === selectedDay) : [];

  const renderNoteContent = (note: PatientNoteTherapist) => (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Note header */}
      <div className="flex items-start justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-white/5 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => setMobileView("nav")}
            className="xl:hidden p-1 -ml-1 shrink-0 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-content-primary truncate">
              {getNoteTitle(note) || t("patient.patientNotes.untitled")}
            </p>
            <p className="text-xs text-content-tertiary mt-0.5">
              {t("patient.patientNotes.sharedOn")}{" "}
              {new Date(`${noteToDateKey(note)}T12:00:00`).toLocaleDateString(i18n.language)}
            </p>
          </div>
        </div>
        {!note.read_by_therapist_at ? (
          <Button size="sm" variant="outline" onClick={handleMarkRead} loading={markingRead} className="shrink-0">
            {t("patient.patientNotes.markRead")}
          </Button>
        ) : (
          <span className="shrink-0 text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
            {t("patient.patientNotes.markedRead")}
          </span>
        )}
      </div>
      {/* Note body + replies side-by-side on desktop, stacked on mobile */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto px-4 py-2 min-w-0">
          <NotionEditor value={note.body} disabled={true} borderless={true} />
        </div>
        {/* Desktop: right panel */}
        <div className="hidden lg:flex flex-col w-72 xl:w-80 shrink-0">
          <NoteRepliesSection
            replies={replies as NoteReply[]}
            loading={repliesLoading}
            submitting={replySubmitting}
            onSubmit={handleSubmitReply}
            currentAuthorType="therapist"
            patientLabel={patientName}
            variant="side"
          />
        </div>
      </div>
      {/* Mobile: stacked below */}
      <div className="lg:hidden">
        <NoteRepliesSection
          replies={replies as NoteReply[]}
          loading={repliesLoading}
          submitting={replySubmitting}
          onSubmit={handleSubmitReply}
          currentAuthorType="therapist"
          patientLabel={patientName}
          variant="bottom"
        />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-[calc(100vh-16rem)] min-h-[420px]">
      <div className="glass-effect glass-border border rounded-2xl shadow-sm overflow-hidden flex flex-1 min-h-0">

        {/* ── Left panel: nav (list or calendar) ─────────────────── */}
        <div className={`${mobileView === "content" ? "hidden" : "flex"} xl:flex w-full xl:w-64 shrink-0 flex-col border-r border-gray-200 dark:border-white/10`}>

          {/* Header: count + view toggle */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10 shrink-0">
            <span className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">
              {notes.length} {notes.length === 1 ? t("patient.patientNotes.note") : t("patient.patientNotes.notes")}
            </span>
            <div className="flex items-center gap-0.5 bg-gray-100 dark:bg-white/10 rounded-lg p-0.5">
              <button
                onClick={() => { setViewMode("list"); setSelected(notes[0] ?? null); setSelectedDay(null); }}
                title={t("portal.notes.listView")}
                className={`p-1.5 rounded-md transition-colors ${viewMode === "list" ? "bg-white dark:bg-white/10 text-brand-600 shadow-sm" : "text-content-tertiary hover:text-gray-600 dark:hover:text-gray-300"}`}
              >
                <List className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => { setViewMode("calendar"); setSelected(null); }}
                title={t("portal.notes.calendarView")}
                className={`p-1.5 rounded-md transition-colors ${viewMode === "calendar" ? "bg-white dark:bg-white/10 text-brand-600 shadow-sm" : "text-content-tertiary hover:text-gray-600 dark:hover:text-gray-300"}`}
              >
                <CalendarDays className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* List or Calendar */}
          {viewMode === "list" ? (
            <ul className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-white/5">
              {notes.map((note) => {
                const isSelected = selected?.id === note.id;
                const title = getNoteTitle(note) || t("patient.patientNotes.untitled");
                const isUnread = !note.read_by_therapist_at;
                return (
                  <li key={note.id}>
                    <button
                      onClick={() => { setSelected(note); setMobileView("content"); }}
                      className={`w-full text-left px-4 py-3 transition-colors border-l-2 ${
                        isSelected
                          ? "bg-brand-50 dark:bg-brand-900/20 border-brand-500"
                          : "hover:bg-gray-50 dark:hover:bg-white/5 border-transparent"
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {isUnread && <span className="w-2 h-2 rounded-full bg-brand-500 shrink-0" />}
                        <p className={`text-sm font-medium truncate ${isSelected ? "text-brand-700 dark:text-brand-300" : "text-gray-800 dark:text-gray-200"}`}>
                          {title}
                        </p>
                      </div>
                      <div className="flex items-center justify-between mt-1 gap-2">
                        <span className="text-xs text-content-tertiary">
                          {new Date(`${noteToDateKey(note)}T12:00:00`).toLocaleDateString(i18n.language)}
                        </span>
                        <span className={`text-xs font-medium ${isUnread ? "text-brand-500" : "text-green-600 dark:text-green-400"}`}>
                          {isUnread ? t("patient.patientNotes.unread") : t("patient.patientNotes.markedRead")}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <CalendarPanel
              notes={notes}
              selectedDay={selectedDay}
              onSelectDay={(day) => { setSelectedDay(day); setSelected(null); setMobileView("content"); }}
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
            />
          )}
        </div>

        {/* ── Right panel: content ─────────────────────────────────── */}
        <div className={`${mobileView === "nav" ? "hidden" : "flex"} xl:flex flex-1 flex-col overflow-hidden`}>
          {selected ? (
            renderNoteContent(selected)
          ) : viewMode === "calendar" && selectedDay ? (
            dayNotes.length === 0 ? (
              <div className="flex flex-col h-full">
                <div className="flex items-center gap-2 px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-white/5 shrink-0">
                  <button
                    onClick={() => setMobileView("nav")}
                    className="xl:hidden p-1 -ml-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <p className="text-sm font-semibold text-content-secondary capitalize">
                    {new Date(`${selectedDay}T12:00:00`).toLocaleDateString(i18n.language, {
                      weekday: "long", day: "numeric", month: "long",
                    })}
                  </p>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center gap-2 text-content-tertiary">
                  <BookOpen className="w-7 h-7 opacity-30" />
                  <p className="text-sm">{t("portal.notes.noNotesThisDay")}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col h-full overflow-hidden">
                <div className="flex items-center gap-2 px-4 sm:px-6 py-3 border-b border-gray-100 dark:border-white/5 shrink-0">
                  <button
                    onClick={() => setMobileView("nav")}
                    className="xl:hidden p-1 -ml-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <p className="text-sm font-semibold text-content-secondary capitalize truncate">
                    {new Date(`${selectedDay}T12:00:00`).toLocaleDateString(i18n.language, {
                      weekday: "long", day: "numeric", month: "long",
                    })}
                  </p>
                </div>
                <ul className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-white/5">
                  {dayNotes.map((note) => {
                    const isUnread = !note.read_by_therapist_at;
                    const title = getNoteTitle(note) || t("patient.patientNotes.untitled");
                    return (
                      <li key={note.id}>
                        <button
                          onClick={() => setSelected(note)}
                          className="w-full text-left px-6 py-4 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {isUnread && <span className="w-2 h-2 rounded-full bg-brand-500 shrink-0" />}
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{title}</p>
                          </div>
                          <div className="flex items-center justify-between mt-1 gap-2">
                            <span className="text-xs text-content-tertiary">
                              {new Date(note.created_at).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" })}
                            </span>
                            <span className={`text-xs font-medium ${isUnread ? "text-brand-500" : "text-green-600 dark:text-green-400"}`}>
                              {isUnread ? t("patient.patientNotes.unread") : t("patient.patientNotes.markedRead")}
                            </span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 text-content-tertiary">
              {viewMode === "calendar"
                ? <><CalendarDays className="w-7 h-7 opacity-30" /><p className="text-sm">{t("portal.notes.selectDay")}</p></>
                : <><BookOpen className="w-7 h-7 opacity-30" /><p className="text-sm">{t("patient.patientNotes.selectNote")}</p></>
              }
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
